import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Search,
  Archive,
  RotateCcw,
  PlayCircle,
  PenSquare,
  ArchiveRestore,
  CopyCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { testrunService } from "../services/testrunService";
import { userService } from "../services/userService";
import TestRunsEmptyState from "../components/testrun/TestRunsEmptyState";
import DataTable from "../components/ui/DataTable";
import toast from "react-hot-toast";
import { useCan } from "../hooks/useCan";
import { useAuth } from "../context/AuthContext";
import MuiSelect from "../components/MuiSelect";

const EditIcon = ({ className, onClick }) => {
  return (
    <PenSquare
      color="#3C3735FF"
      className="w-8 h-8 mr-1 px-2 py-1 rounded-full bg-[#F5F4F3FF] hover:cursor-pointer"
      onClick={onClick}
    />
  );
};

const DeleteIcon = ({ className, onClick }) => {
  return (
    <Trash2
      color="#FFFFFF"
      className="w-8 h-8 mr-1 px-2 py-1 rounded-full bg-[#DE3B40FF] hover:cursor-pointer"
      onClick={onClick}
    />
  );
};

const RestoreIcon = ({ className, onClick }) => {
  return (
    <ArchiveRestore
      color="#A17561FF"
      className="w-8 h-8 mr-1 px-2 py-1 rounded-full bg-[#F5F4F3FF] hover:cursor-pointer"
      onClick={onClick}
    />
  );
};

const ArchiveIcon = ({ className, onClick }) => {
  return (
    <Archive
      color="#DE3B40FF"
      className="w-8 h-8 mr-1 px-2 py-1 rounded-full bg-[#F5F4F3FF] hover:cursor-pointer"
      onClick={onClick}
    />
  );
};

const ExecuteIcon = ({ className, onClick }) => {
  return (
    <PlayCircle
      color="#117B34FF"
      className="w-8 h-8 mr-1 px-2 py-1 rounded-full bg-[#82EEA6FF] hover:cursor-pointer"
      onClick={onClick}
    />
  );
};

const CloneIcon = ({ className, onClick }) => {
  return (
    <CopyCheck
      color="#3C3735FF"
      className="w-8 h-8 mr-1 px-2 py-1 rounded-full bg-[#F5F4F3FF] hover:cursor-pointer"
      onClick={onClick}
    />
  );
};

const TestRunManagementPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "Project";

  const [testRuns, setTestRuns] = useState([]);
  const [filteredTestRuns, setFilteredTestRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTestRun, setSelectedTestRun] = useState(null);
  const [cloningTestRun, setCloningTestRun] = useState(false);

  // Filters and search
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignToFilter, setAssignToFilter] = useState("all");
  const [sortBy, setSortBy] = useState("most_recent");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [users, setUsers] = useState([]);

  const [assigningTestRun, setAssigningTestRun] = useState(null);

  const { user } = useAuth();

  // Permission checks
  const { can: canCreateTestRun } = useCan("runs_plan", "add_edit");
  const { can: canEditTestRun } = useCan("runs_plan", "add_edit");
  const { can: canDeleteTestRun } = useCan("runs_plan", "permanently_delete");
  const { can: canViewTestRun } = useCan("runs_plan", "view");

  const loadTestRuns = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const filters = {};
      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }
      if (assignToFilter !== "all") {
        filters.assigned_to =
          assignToFilter === "me" ? user?.user_id : assignToFilter;
      }

      const response = await testrunService.listTestRuns(projectId, filters);
      if (response.success) {
        let runs = response.data?.test_runs || [];

        // Apply search filter
        if (searchQuery) {
          runs = runs.filter(
            (run) =>
              run.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              run.description
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              run.references?.toLowerCase().includes(searchQuery.toLowerCase()),
          );
        }

        // Apply sorting
        if (sortBy === "most_recent") {
          runs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        } else if (sortBy === "name") {
          runs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        } else if (sortBy === "status") {
          runs.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
        }

        setTestRuns(runs);
        setFilteredTestRuns(runs);
      }
    } catch (error) {
      console.error("Error loading test runs:", error);
      toast.error("Failed to load test runs");
    } finally {
      setLoading(false);
    }
  }, [projectId, statusFilter, assignToFilter, sortBy, searchQuery, user]);

  const loadUsers = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await userService.findUsers({
        project_id: projectId,
      });
      if (response.success) {
        setUsers(response.data?.users || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, [projectId]);

  useEffect(() => {
    loadTestRuns();
  }, [loadTestRuns]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleMenuOpen = (testRun) => {
    setSelectedTestRun(testRun);
  };

  const handleMenuClose = () => {
    setSelectedTestRun(null);
  };

  const handleEdit = (testRun) => {
    handleMenuClose();
    navigate(
      `/test-runs/new?project_id=${projectId}&project_name=${projectName}&test_run_id=${testRun.test_run_id}&mode=edit`,
    );
  };

  /** Pass `testRun` from the row when deleting from the menu — `setState` is async, so relying on `selectedTestRun` in the same click handler often leaves it stale/null. */
  const handleDelete = async (testRunParam) => {
    const testRun = testRunParam ?? selectedTestRun;
    if (!testRun) return;
    if (
      !window.confirm(
        `Are you sure you want to delete test run "${testRun.name}"?`,
      )
    ) {
      handleMenuClose();
      return;
    }
    try {
      await testrunService.deleteTestRun(testRun.test_run_id, projectId);
      toast.success("Test run deleted successfully");
      loadTestRuns();
    } catch (error) {
      console.error("Error deleting test run:", error);
      toast.error(error.response?.data?.message || "Failed to delete test run");
    }
    handleMenuClose();
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select test runs to delete");
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedRows.length} test run(s)?`,
      )
    ) {
      return;
    }
    try {
      for (const testRunId of selectedRows) {
        await testrunService.deleteTestRun(testRunId, projectId);
      }
      toast.success(`${selectedRows.length} test run(s) deleted successfully`);
      setSelectedRows([]);
      loadTestRuns();
    } catch (error) {
      console.error("Error bulk deleting test runs:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete test runs",
      );
    }
  };

  const handleBulkArchive = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select test runs to archive");
      return;
    }
    try {
      await testrunService.bulkArchiveTestRuns(selectedRows, projectId);
      toast.success(`${selectedRows.length} test run(s) archived successfully`);
      setSelectedRows([]);
      loadTestRuns();
    } catch (error) {
      console.error("Error bulk archiving test runs:", error);
      toast.error(
        error.response?.data?.message || "Failed to archive test runs",
      );
    }
  };

  const handleBulkRestore = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select test runs to restore");
      return;
    }
    try {
      await testrunService.bulkRestoreTestRuns(selectedRows, projectId);
      toast.success(`${selectedRows.length} test run(s) restored successfully`);
      setSelectedRows([]);
      loadTestRuns();
    } catch (error) {
      console.error("Error bulk restoring test runs:", error);
      toast.error(
        error.response?.data?.message || "Failed to restore test runs",
      );
    }
  };

  const handleArchive = async (testRun) => {
    try {
      await testrunService.archiveTestRun(testRun.test_run_id, projectId);
      toast.success("Test run archived successfully");
      loadTestRuns();
    } catch (error) {
      console.error("Error archiving test run:", error);
      toast.error(
        error.response?.data?.message || "Failed to archive test run",
      );
    }
    handleMenuClose();
  };

  const handleRestore = async (testRun) => {
    try {
      await testrunService.restoreTestRun(testRun.test_run_id, projectId);
      toast.success("Test run restored successfully");
      loadTestRuns();
    } catch (error) {
      console.error("Error restoring test run:", error);
      toast.error(
        error.response?.data?.message || "Failed to restore test run",
      );
    }
    handleMenuClose();
  };

  const handleClone = async (testRunParam) => {
    const testRun = testRunParam ?? selectedTestRun;
    if (!testRun) return;
    handleMenuClose();

    try {
      setCloningTestRun(true);
      navigate(
        `/test-runs/new?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}&test_run_id=${testRun.test_run_id}&mode=clone`,
      );
    } catch (error) {
      console.error("Error cloning test run:", error);
      toast.error(error.response?.data?.message || "Failed to clone test run");
    } finally {
      setCloningTestRun(false);
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      active: "default",
      completed: "default",
      cancelled: "destructive",
      on_hold: "secondary",
      archive: "secondary",
    };
    return variants[status] || "outline";
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      on_hold: "bg-yellow-100 text-yellow-800",
      archive: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          checked={
            selectedRows.length > 0 &&
            selectedRows.length === filteredTestRuns.length &&
            filteredTestRuns.length > 0
          }
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows(filteredTestRuns.map((r) => r.test_run_id));
            } else {
              setSelectedRows([]);
            }
          }}
          className="w-4 h-4"
        />
      ),
      render: (_, row) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.test_run_id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.test_run_id]);
            } else {
              setSelectedRows(
                selectedRows.filter((id) => id !== row.test_run_id),
              );
            }
          }}
          className="w-4 h-4"
        />
      ),
    },
    {
      key: "name",
      header: "RUN NAME",
      render: (value, row) => (
        <div>
          <div className="font-medium text-sm text-text-primary">{value}</div>
          {row.external_id && (
            <div className="text-xs text-text-secondary mt-0.5">
              ID: {row.external_id}
            </div>
          )}
          {row.description && (
            <div className="text-xs text-text-secondary mt-1 line-clamp-1">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "references",
      header: "REFERENCE",
      render: (value) => {
        if (!value) return "-";
        const refs = value.split(",").map((r) => r.trim());
        return (
          <div className="flex flex-wrap gap-1">
            {refs.map((ref, idx) => (
              <a
                key={idx}
                href={`#${ref}`}
                className="text-blue-600 hover:underline text-sm"
                onClick={(e) => {
                  e.preventDefault();
                  // Handle reference click (e.g., open Jira link)
                }}
              >
                {ref}
              </a>
            ))}
          </div>
        );
      },
    },
    {
      key: "assigned_to_name",
      header: "ASSIGN TO",
      render: (value) => value || "Unassigned",
    },
    {
      key: "test_plan",
      header: "TEST PLAN",
      render: (value, row) => {
        if (
          !row.test_plan_ids ||
          !Array.isArray(row.test_plan_ids) ||
          row.test_plan_ids.length === 0
        ) {
          return "-";
        }
        // Format test plan IDs (e.g., ["TP-12", "TP-942"] -> "TP-12, TP-942")
        const planIds = row.test_plan_ids.map((id) => {
          // If it's already formatted like "TP-12", use it as is
          // Otherwise, format it
          return id.startsWith("TP-") ? id : `TP-${id}`;
        });
        return (
          <div className="flex flex-wrap gap-1">
            {planIds.map((planId, idx) => (
              <a
                key={idx}
                href={`#${planId}`}
                className="text-blue-600 hover:underline text-sm"
                onClick={(e) => {
                  e.preventDefault();
                  // Handle test plan click (e.g., navigate to test plan details)
                }}
              >
                {planId}
              </a>
            ))}
          </div>
        );
      },
    },
    {
      key: "test_case_count",
      header: "TEST CASES",
      render: (value) => value || 0,
    },
    {
      key: "status",
      header: "STATUS",
      render: (value) => (
        <Badge
          variant={getStatusVariant(value)}
          className={getStatusColor(value)}
        >
          {value === "archive" ? "Archive" : value}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "ACTIONS",
      render: (_, row) => (
        <div className="flex flex-row gap-1 items-center justify-left">
          {row.status === "archived" ? (
            <>
              {canViewTestRun && (
                <RestoreIcon onClick={() => handleRestore(row)} />
              )}
              {canDeleteTestRun && (
                <DeleteIcon onClick={() => handleDelete(row)} />
              )}
            </>
          ) : row.status === "completed" || row.status === "on_hold" ? (
            <>
              {canViewTestRun && (
                <ExecuteIcon
                  onClick={() =>
                    navigate(
                      `/test-run-execution?test_run_id=${row.test_run_id}&project_id=${projectId}&project_name=${projectName}`,
                    )
                  }
                />
              )}
              {canCreateTestRun && (
                <CloneIcon onClick={() => handleClone(row)} />
              )}
              {canEditTestRun && (
                <ArchiveIcon onClick={() => handleArchive(row)} />
              )}
              {canDeleteTestRun && (
                <DeleteIcon onClick={() => handleDelete(row)} />
              )}
            </>
          ) : (
            <>
              {canViewTestRun && (
                <ExecuteIcon
                  onClick={() =>
                    navigate(
                      `/test-run-execution?test_run_id=${row.test_run_id}&project_id=${projectId}&project_name=${projectName}`,
                    )
                  }
                />
              )}
              {canEditTestRun && <EditIcon onClick={() => handleEdit(row)} />}
              {canCreateTestRun && (
                <CloneIcon onClick={() => handleClone(row)} />
              )}
              {canEditTestRun && (
                <ArchiveIcon onClick={() => handleArchive(row)} />
              )}
              {canDeleteTestRun && (
                <DeleteIcon onClick={() => handleDelete(row)} />
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  if (!projectId) {
    return (
      <div className="p-6">
        <div className="text-red-600 text-lg font-semibold">
          Project ID is required
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-text-secondary mb-4">
        <button
          onClick={() => navigate("/projects")}
          className="hover:text-primary-600 transition-colors"
        >
          Projects
        </button>
        <span>/</span>
        <button
          onClick={() =>
            navigate(
              `/test-cases?project_id=${projectId}&project_name=${projectName}`,
            )
          }
          className="hover:text-primary-600 transition-colors"
        >
          {projectName}
        </button>
        <span>/</span>
        <span className="text-text-primary">Test Runs</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-text-primary">
          Test Runs & Results
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() =>
              navigate(
                `/test-runs/new?project_id=${projectId}&project_name=${projectName}`,
              )
            }
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown transition-colors whitespace-nowrap flex-shrink-0"
          >
            <Plus className="w-4 h-4 flex-shrink-0" />
            <span>Test Runs</span>
          </button>
          {canCreateTestRun && (
            <button
              onClick={() =>
                navigate(
                  `/test-plans/new?project_id=${projectId}&project_name=${projectName}`,
                )
              }
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown-inverted transition-colors whitespace-nowrap flex-shrink-0"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span>Test Plans</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <Card className="p-4 mb-4 border border-gray-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-4">
          {/* Bulk Actions */}
          {selectedRows.length > 0 && (
            <div className="flex gap-2">
              {canDeleteTestRun && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
              {canEditTestRun && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkArchive}
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkRestore}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Filters - Always visible */}
          <div className="flex flex-wrap items-center gap-3 ml-auto">
            <MuiSelect
              label="Assign To"
              value={assignToFilter}
              onFilterChange={setAssignToFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Me", value: "me" },
                ...users.map((user) => ({
                  label: user.full_name || user.email,
                  value: user.user_id,
                })),
              ]}
              sx={{ minWidth: "100px" }}
              className="bg-white"
            />

            <MuiSelect
              label="Sort By"
              value={sortBy}
              onFilterChange={setSortBy}
              options={[
                { label: "Most Recent", value: "most_recent" },
                { label: "Name", value: "name" },
                { label: "Status", value: "status" },
              ]}
              sx={{ minWidth: "100px" }}
              className="bg-white"
            />

            <MuiSelect
              label="Status"
              value={statusFilter}
              onFilterChange={setStatusFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Active", value: "active" },
                { label: "Completed", value: "completed" },
                { label: "Archive", value: "archive" },
                { label: "On Hold", value: "on_hold" },
                { label: "Cancelled", value: "cancelled" },
              ]}
              sx={{ minWidth: "100px" }}
              className="bg-white"
            />

            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search test runs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-4 w-[320px]"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Test Runs Table */}
      {testRuns.length === 0 && !loading ? (
        // Show two-card empty state when there are no test runs at all
        <TestRunsEmptyState
          onCreateTestRun={() => {
            navigate(
              `/test-runs/new?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}`,
            );
          }}
          onCreateTestPlan={() =>
            navigate(
              `/test-plans?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}`,
            )
          }
          canCreateTestRun={canCreateTestRun}
        />
      ) : (
        // Show table with simple empty state when filters return no results
        <Card className="border border-gray-200 rounded-lg overflow-hidden">
          <DataTable
            data={filteredTestRuns}
            columns={columns}
            loading={loading}
            emptyStateContent={
              <div className="flex flex-col items-center justify-center py-16">
                <p className="text-base font-semibold text-text-primary mb-2">
                  No test runs found
                </p>
                <p className="text-sm text-text-secondary mb-6">
                  Create your first test run to get started.
                </p>
                {canCreateTestRun && (
                  <Button
                    onClick={() => {
                      navigate(
                        `/test-runs/new?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}`,
                      );
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />+ Add Test Runs
                  </Button>
                )}
              </div>
            }
          />
        </Card>
      )}
    </div>
  );
};

export default TestRunManagementPage;
